import { ref, watch, nextTick, type Ref } from "vue";
import noop from "lodash/noop";

export interface PositionState {
  isFlippedVertical: boolean;
  isFlippedHorizontal: boolean;
  maxHeight?: number;
  maxWidth?: number;
}

export interface OverflowDetectionResult {
  shouldFlipVertical?: boolean;
  shouldFlipHorizontal?: boolean;
  needsHeightConstraint?: boolean;
  needsWidthConstraint?: boolean;
  maxHeight?: number;
  maxWidth?: number;
}

export interface OverflowDetectionOptions {
  /** The scrollable container ID to consider for boundaries */
  scrollableContainerId?: string;
  /** Offset to apply when positioning (in pixels) */
  offset?: { top?: number; left?: number; right?: number; bottom?: number };
  /** Whether to check for overflow against the viewport */
  checkViewportOverflow?: boolean;
  /** Whether to enable auto-sizing when content doesn't fit */
  enableAutoSizing?: boolean;
  /** Minimum space to maintain (in pixels) */
  minSpace?: number;
}

/**
 * Composable for detecting and handling overflow positioning
 *
 * @param targetRef Reference to the element that needs positioning
 * @param triggerRef Reference to the element that triggers the target's visibility
 * @param isVisible Reactive reference to whether the target is visible
 * @param options Configuration options
 * @returns Positioning state and utility functions
 */
export function useOverflowDetection<
  T extends HTMLElement,
  U extends HTMLElement
>(
  targetRef: Ref<T | null>,
  triggerRef: Ref<U | null>,
  isVisible: Ref<boolean>,
  options: OverflowDetectionOptions = {}
) {
  const {
    scrollableContainerId,
    offset = { top: 10, left: 0, right: 0, bottom: 10 },
    checkViewportOverflow = true,
    enableAutoSizing = true,
    minSpace = 10
  } = options;

  // State for positioning
  const positionState = ref<PositionState>({
    isFlippedVertical: false,
    isFlippedHorizontal: false,
    maxHeight: undefined,
    maxWidth: undefined
  });

  // Coordinates for absolute positioning
  const coordinates = ref({
    top: 0,
    left: 0
  });

  /**
   * Detect if target element is overflowing its container or viewport
   */
  const detectOverflow = (): OverflowDetectionResult => {
    if (!targetRef.value || !triggerRef.value) return {};

    // Get element dimensions
    const targetRect = targetRef.value.getBoundingClientRect();
    const triggerRect = triggerRef.value.getBoundingClientRect();

    // Viewport boundaries
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Set up container boundaries
    let containerRect:
      | DOMRect
      | {
          top: number;
          right: number;
          bottom: number;
          left: number;
        } = {
      top: 0,
      right: viewportWidth,
      bottom: viewportHeight,
      left: 0
    };

    if (scrollableContainerId) {
      // If a specific scrollable container is provided, use its boundaries
      const containerElement = document.getElementById(scrollableContainerId);
      if (containerElement) {
        containerRect = containerElement.getBoundingClientRect();
      } else {
        // Fallback to viewport if container not found
        containerRect = {
          top: 0,
          right: viewportWidth,
          bottom: viewportHeight,
          left: 0
        };
      }
    } else if (!checkViewportOverflow) {
      // For non-viewport checks, find the nearest scrollable/positioned parent
      let parentElement = triggerRef.value.parentElement;
      let foundContainer = false;

      while (parentElement) {
        const style = window.getComputedStyle(parentElement);
        if (
          style.overflow !== "visible" ||
          style.position === "relative" ||
          style.position === "absolute"
        ) {
          containerRect = parentElement.getBoundingClientRect();
          foundContainer = true;
          break;
        }
        parentElement = parentElement.parentElement;
      }

      // If no suitable parent found, use document body
      if (!foundContainer) {
        containerRect = document.body.getBoundingClientRect();
      }
    } else {
      // When checking viewport overflow, use viewport boundaries
      containerRect = {
        top: 0,
        right: viewportWidth,
        bottom: viewportHeight,
        left: 0
      };
    }

    // Calculate available space in all directions based on container type
    const spaceAbove = checkViewportOverflow
      ? triggerRect.top
      : triggerRect.top - Math.max(0, containerRect.top);

    const spaceBelow = checkViewportOverflow
      ? viewportHeight - triggerRect.bottom
      : containerRect.bottom - triggerRect.bottom;

    const spaceLeft = checkViewportOverflow
      ? triggerRect.left
      : triggerRect.left - Math.max(0, containerRect.left);

    const spaceRight = checkViewportOverflow
      ? viewportWidth - triggerRect.right
      : containerRect.right - triggerRect.right;

    // Check if target overflows in any direction
    const overflowsBottom =
      targetRect.height > spaceBelow - (offset.bottom || 0);
    const overflowsRight = targetRect.width > spaceRight - (offset.right || 0);

    // Determine if we should flip (only flip if there's more space in the other direction)
    const shouldFlipVertical = overflowsBottom && spaceAbove > spaceBelow;
    const shouldFlipHorizontal = overflowsRight && spaceLeft > spaceRight;

    // If target is too large for either direction, we'll need to constrain its size
    const needsHeightConstraint =
      enableAutoSizing && targetRect.height > Math.max(spaceAbove, spaceBelow);
    const needsWidthConstraint =
      enableAutoSizing && targetRect.width > Math.max(spaceLeft, spaceRight);

    return {
      shouldFlipVertical,
      shouldFlipHorizontal,
      needsHeightConstraint,
      needsWidthConstraint,
      maxHeight: needsHeightConstraint
        ? Math.max(
            minSpace,
            (shouldFlipVertical ? spaceAbove : spaceBelow) - minSpace
          )
        : undefined,
      maxWidth: needsWidthConstraint
        ? Math.max(
            minSpace,
            (shouldFlipHorizontal ? spaceLeft : spaceRight) - minSpace
          )
        : undefined
    };
  };

  /**
   * Update position based on overflow detection
   */
  const updatePosition = (): void => {
    if (!isVisible.value || !targetRef.value || !triggerRef.value) return;

    // Use nextTick to ensure the target is rendered before measuring
    nextTick(() => {
      const result = detectOverflow();
      const triggerRect = triggerRef.value?.getBoundingClientRect();

      if (!triggerRect) return;

      positionState.value = {
        isFlippedVertical: !!result.shouldFlipVertical,
        isFlippedHorizontal: !!result.shouldFlipHorizontal,
        maxHeight: result.needsHeightConstraint ? result.maxHeight : undefined,
        maxWidth: result.needsWidthConstraint ? result.maxWidth : undefined
      };

      // Calculate coordinates for absolute positioning
      if (result.shouldFlipVertical) {
        coordinates.value.top =
          triggerRect.top -
          (targetRef.value?.offsetHeight || 0) -
          (offset.top || 0);
      } else {
        coordinates.value.top = triggerRect.bottom + (offset.bottom || 0);
      }

      if (result.shouldFlipHorizontal) {
        coordinates.value.left =
          triggerRect.right - (targetRef.value?.offsetWidth || 0);
      } else {
        coordinates.value.left = triggerRect.left;
      }
    });
  };

  /**
   * Set up container scroll listener with throttling to improve performance
   */
  const setupContainerScrollListener = (): (() => void) => {
    if (!scrollableContainerId) {
      return noop;
    }

    // Add throttling to avoid performance issues with frequent updates
    let lastScrollTime = 0;
    const throttleTime = 10; // ms

    const throttledUpdatePosition = () => {
      const now = Date.now();
      if (now - lastScrollTime >= throttleTime) {
        lastScrollTime = now;
        updatePosition();
      }
    };

    const containerElement = document.getElementById(scrollableContainerId);
    if (containerElement) {
      containerElement.addEventListener("scroll", throttledUpdatePosition, {
        passive: true
      });
      return () =>
        containerElement.removeEventListener("scroll", throttledUpdatePosition);
    }
    return noop;
  };

  // Watch for visibility changes to update position
  watch(isVisible, (newIsVisible) => {
    if (newIsVisible) {
      updatePosition();
    }
  });

  // Set up event listeners
  const setupEventListeners = () => {
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, { passive: true });
    const removeScrollListener = setupContainerScrollListener();

    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition);
      removeScrollListener();
    };
  };

  // Computed style for the target element
  const getTargetStyle = () => {
    const baseStyle = {
      maxHeight: positionState.value.maxHeight
        ? `${positionState.value.maxHeight}px`
        : undefined,
      maxWidth: positionState.value.maxWidth
        ? `${positionState.value.maxWidth}px`
        : undefined,
      overflowY: positionState.value.maxHeight ? ("auto" as const) : undefined,
      overflowX: positionState.value.maxWidth ? ("auto" as const) : undefined
    };

    // For absolute positioning (teleporting)
    const positionStyle = {
      ...baseStyle,
      top: `${coordinates.value.top}px`,
      left: `${coordinates.value.left}px`
    };

    return {
      baseStyle,
      positionStyle
    };
  };

  return {
    positionState,
    coordinates,
    updatePosition,
    setupEventListeners,
    getTargetStyle
  };
}
